import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, InputGroup, Card, Modal, Row, Col, Badge, Spinner, Pagination, Tab, Tabs } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faPlus, faEdit, faTrash, faBoxes, 
  faSync, faArrowDown, faArrowUp, faHistory
} from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../../layouts/MainLayout';
import ErrorBoundary from '../../components/ErrorBoundary';
import EmptyStateMessage from '../../components/EmptyStateMessage';

const DEBUG = true; // Ubah ke false di lingkungan produksi

// Tambahkan di bagian atas file atau sebagai utility
const apiRequest = async (url, options = {}) => {
  const startTime = performance.now();
  try {
    const response = await axios(url, options);
    const endTime = performance.now();
    console.log(`API call to ${url} took ${Math.round(endTime - startTime)}ms`);
    return response;
  } catch (error) {
    const endTime = performance.now();
    console.error(`API call to ${url} failed after ${Math.round(endTime - startTime)}ms:`, error);
    throw error;
  }
};

const logDebug = (...args) => {
  if (DEBUG) {
    console.log('[DEBUG]', ...args);
  }
};

const showDetailedError = (error) => {
  let message = 'Terjadi kesalahan';
  
  if (error.response) {
    // Server merespons dengan status kode di luar range 2xx
    const status = error.response.status;
    const data = error.response.data;
    
    if (typeof data === 'object' && data !== null) {
      message = `Error ${status}: ${JSON.stringify(data)}`;
    } else if (typeof data === 'string') {
      message = `Error ${status}: ${data}`;
    } else {
      message = `Error ${status}`;
    }
  } else if (error.request) {
    // Request dibuat tapi tidak ada respons
    message = 'Server tidak merespons permintaan';
  } else {
    // Kesalahan saat setup request
    message = error.message;
  }
  
  toast.error(message, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

// Helper functions
const normalizeNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    if (cleaned === '' || cleaned === '.' || cleaned === '-') return 0;
    
    const number = parseFloat(cleaned);
    return isNaN(number) ? 0 : number;
  }
  
  return 0;
};

const formatRupiah = (value) => {
  if (value === undefined || value === null || value === '') {
    return 'Rp 0';
  }
  
  let numValue;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    numValue = cleaned ? parseFloat(cleaned) : 0;
  } else {
    numValue = Number(value);
  }
  
  if (isNaN(numValue)) {
    console.warn('Invalid number format for value:', value);
    return 'Rp 0';
  }
  
  try {
    return `Rp ${numValue.toLocaleString('id-ID')}`;
  } catch (err) {
    console.error('Error formatting number:', err);
    return `Rp ${numValue}`;
  }
};

// Fetch with auth helper
const fetchWithAuth = async (url, options = {}) => {
  try {
    const token = localStorage.getItem('jwtToken');
    
    if (!token) {
      toast.error('Sesi login telah berakhir');
      window.location.href = '/login';
      throw new Error('Token tidak ditemukan');
    }
    
    const method = options.method || 'GET';
    
    logDebug(`Axios ${method} to ${url}`);
    
    const response = await axios({
      url,
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json'
      },
      data: options.body ? JSON.parse(options.body) : undefined
    });
    
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      json: async () => response.data
    };
  } catch (error) {
    console.error('Axios error:', error);
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('jwtToken');
      toast.error('Sesi Anda telah berakhir, silakan login kembali');
      window.location.href = '/login';
    }
    throw error;
  }
};

const updateInventoryStock = async (inventoryId, itemName, previousStock, newStock) => {
  try {
    const token = localStorage.getItem('jwtToken');
    
    // Jika tidak ada perubahan stok, tidak perlu update
    if (previousStock === newStock) {
      return { success: true, message: 'Tidak ada perubahan stok' };
    }

    console.log(`Updating stock for ${itemName}: ${previousStock} -> ${newStock}`);
    
    // Coba beberapa endpoint berbeda jika yang pertama gagal
    const endpoints = [
      `https://rumahakrilik.id/api/inventory/${inventoryId}/manual-update/`,
      `https://rumahakrilik.id/inventory/${inventoryId}/manual-update/`,
      `https://rumahakrilik.id/api/inventory-manual-adjustments/`
    ];
    
    for (const endpoint of endpoints) {
      try {
        const updateResponse = await axios.post(
          endpoint,
          {
            inventory_id: inventoryId,
            current_stock: newStock,
            reason: `Penyesuaian stok manual dari ${previousStock} ke ${newStock}`
          },
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache'
            } 
          }
        );
        
        if (updateResponse.status === 200) {
          console.log('Update response:', updateResponse.data);
          return { 
            success: true, 
            message: `Stok berhasil diperbarui: ${previousStock} → ${newStock}` 
          };
        }
      } catch (endpointError) {
        console.error(`Failed with endpoint ${endpoint}:`, endpointError);
      }
    }
    
    // Jika semua endpoint gagal, coba dengan PATCH langsung
    try {
      const patchResponse = await axios.patch(
        `https://rumahakrilik.id/api/inventory/${inventoryId}/`,
        { current_stock: newStock },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          } 
        }
      );
      
      if (patchResponse.status === 200) {
        return { 
          success: true, 
          message: `Stok berhasil diperbarui dengan metode alternatif: ${previousStock} → ${newStock}` 
        };
      }
    } catch (patchError) {
      console.error('PATCH update failed:', patchError);
    }
    
    throw new Error(`Semua upaya update stok gagal`);
  } catch (error) {
    console.error('Error updating stock:', error);
    
    if (error.response && error.response.data) {
      throw new Error(`Gagal update stok: ${JSON.stringify(error.response.data)}`);
    }
    throw error;
  }
};

const DaftarStockBahan = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'raw_material',    
    unit: 'pcs',
    current_stock: '0',
    minimum_stock: '0',
    price: '0',
    location: '',
    notes: ''
  });

  // Tambahkan state pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalItems: 0,
    totalPages: 1
  });

  // Tambahkan state untuk active tab
  const [activeTab, setActiveTab] = useState('raw_material');

  // Tambahkan state untuk modal histori
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyItem, setHistoryItem] = useState({
    id: null,
    name: ''
  });

  // Tambahkan state untuk filter aktif
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'empty', 'low', 'normal'

  // Tambahkan state untuk filter status di atas activeTab
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'empty', 'low', 'normal'

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (editMaterial) {
      setFormData({
        name: editMaterial.name || '',
        category: editMaterial.category || 'raw_material',
        unit: editMaterial.unit || 'pcs',
        current_stock: String(editMaterial.current_stock || 0),
        minimum_stock: String(editMaterial.minimum_stock || 0),
        price: String(editMaterial.price || 0),
        location: editMaterial.location || '',
        notes: editMaterial.notes || ''
      });
    }
  }, [editMaterial]);

  // Tambahkan useEffect untuk merefresh data saat tab berubah
  useEffect(() => {
    fetchMaterials(true);
  }, [activeTab]);

  const fetchMaterials = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }
  
      // Gunakan pendekatan seperti di StockProdukJadi.jsx dengan axios langsung
      const response = await axios.get(`https://rumahakrilik.id/api/inventory/`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': forceRefresh ? 'no-cache' : '',
          'Pragma': forceRefresh ? 'no-cache' : ''
        }
      });
  
      console.log('Raw API response:', response);
  
      if (response.status === 200) {
        // Pastikan response.data adalah array
        let materialsList = [];
        if (Array.isArray(response.data)) {
          materialsList = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          materialsList = response.data.results;
        }
  
        // Filter berdasarkan activeTab (kategori)
        const filteredByCategory = materialsList.filter(item => 
          item.category === activeTab || 
          (activeTab === 'raw_material' && !item.category) // Default untuk item tanpa kategori
        );
        
        console.log(`Filtered ${filteredByCategory.length} materials from ${materialsList.length} total`);
        
        if (filteredByCategory.length === 0) {
          setMaterials([]);
          setLoading(false);
          return;
        }
  
        // Standardisasi data seperti di StockProdukJadi.jsx
        const standardizedMaterials = filteredByCategory.map(material => ({
          id: material.id,
          name: material.name || 'Tidak ada nama',
          category: material.category || 'raw_material',
          current_stock: material.current_stock !== undefined ? material.current_stock : 0,
          minimum_stock: material.minimum_stock !== undefined ? material.minimum_stock : 0,
          price: material.price !== undefined ? material.price : 0,
          unit: material.unit || 'pcs',
          location: material.location || '-',
          notes: material.notes || '-'
        }));
  
        // Sortir hasil
        const sortedMaterials = standardizedMaterials.sort((a, b) => {
          // Prioritas 1: Tampilkan yang stoknya 0 dulu
          if (a.current_stock === 0 && b.current_stock !== 0) return -1;
          if (a.current_stock !== 0 && b.current_stock === 0) return 1;
          
          // Prioritas 2: Tampilkan yang stoknya di bawah minimum
          const aIsBelowMinimum = a.current_stock < a.minimum_stock;
          const bIsBelowMinimum = b.current_stock < b.minimum_stock;
          
          if (aIsBelowMinimum && !bIsBelowMinimum) return -1;
          if (!aIsBelowMinimum && bIsBelowMinimum) return 1;
          
          // Prioritas 3: Urutkan berdasarkan stok terendah
          return a.current_stock - b.current_stock;
        });
        
        setMaterials(sortedMaterials);
      } else {
        toast.error(`Error status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      
      // Implementasi fallback seperti di DaftarAset.jsx
      try {
        console.log('Trying alternative endpoint...');
        const token = localStorage.getItem('jwtToken');
        
        // Coba dengan endpoint spesifik untuk bahan baku
        const fallbackResponse = await axios.get('https://rumahakrilik.id/api/inventory/?type=material', {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
        if (fallbackResponse.status === 200) {
          // Proses data seperti di atas
          let materialsList = [];
          if (Array.isArray(fallbackResponse.data)) {
            materialsList = fallbackResponse.data;
          } else if (fallbackResponse.data.results && Array.isArray(fallbackResponse.data.results)) {
            materialsList = fallbackResponse.data.results;
          }
          
          // Filter & format data
          const filteredMaterials = materialsList.filter(item => item.category === activeTab);
          const standardizedMaterials = filteredMaterials.map(material => ({
            id: material.id,
            name: material.name || 'Tidak ada nama',
            category: material.category || 'raw_material',
            current_stock: material.current_stock !== undefined ? material.current_stock : 0,
            minimum_stock: material.minimum_stock !== undefined ? material.minimum_stock : 0,
            price: material.price !== undefined ? material.price : 0,
            unit: material.unit || 'pcs',
            location: material.location || '-',
            notes: material.notes || '-'
          }));
          
          // Sortir hasil
          const sortedMaterials = standardizedMaterials.sort((a, b) => {
            if (a.current_stock === 0 && b.current_stock !== 0) return -1;
            if (a.current_stock !== 0 && b.current_stock === 0) return 1;
            
            const aIsBelowMinimum = a.current_stock < a.minimum_stock;
            const bIsBelowMinimum = b.current_stock < b.minimum_stock;
            
            if (aIsBelowMinimum && !bIsBelowMinimum) return -1;
            if (!aIsBelowMinimum && bIsBelowMinimum) return 1;
            
            return a.current_stock - b.current_stock;
          });
          
          setMaterials(sortedMaterials);
          toast.info('Data diambil dengan metode alternatif');
        } else {
          toast.error('Semua metode pengambilan data gagal');
          setMaterials([]);
        }
      } catch (fallbackError) {
        console.error('Fallback method failed:', fallbackError);
        toast.error('Gagal memuat data bahan baku');
        setMaterials([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!validateForm()) return;
      
      const materialData = {
        name: formData.name.trim(),
        sku: `MAT-${Date.now().toString().slice(-6)}`,
        category: formData.category,
        unit: formData.unit,
        current_stock: parseInt(formData.current_stock, 10) || 0,
        minimum_stock: parseInt(formData.minimum_stock, 10) || 0,
        price: parseInt(formData.price.replace(/[^\d]/g, ''), 10) || 0,
        location: formData.location.trim(),
        notes: formData.notes.trim()
      };
      
      logDebug("Data bahan baku yang akan dikirim:", materialData);
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        throw new Error('Token tidak tersedia, silakan login kembali');
      }
      
      const response = await fetch('https://rumahakrilik.id/api/inventory/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(materialData)
      });
      
      logDebug("Raw create response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Create error response:", errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(`Server Error: ${JSON.stringify(errorJson)}`);
        } catch (e) {
          throw new Error(`Server Error: ${errorText} (Status: ${response.status})`);
        }
      }
      
      const responseData = await response.json();
      logDebug("Create response data:", responseData);
      
      toast.success('Bahan baku berhasil ditambahkan');
      setShowModal(false);
      
      setTimeout(() => fetchMaterials(true), 1000);
    } catch (error) {
      console.error('Error creating material:', error);
      toast.error(error.message || 'Gagal menambahkan bahan baku');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      const previousStock = parseInt(editMaterial.current_stock, 10) || 0;
      const newStock = parseInt(formData.current_stock, 10) || 0;
      
      // Jika stok berubah, gunakan metode dari StockDiproses.jsx
      if (previousStock !== newStock) {
        toast.info('Memperbarui stok...');
        
        try {
          const result = await updateInventoryStock(
            editMaterial.id,
            editMaterial.name,
            previousStock,
            newStock
          );
          
          if (result.success) {
            toast.success(result.message);
            
            // Update materials array untuk tampilan tabel
            setMaterials(prevMaterials => 
              prevMaterials.map(item => 
                item.id === editMaterial.id 
                  ? {...item, current_stock: newStock} 
                  : item
              )
            );
            
            // Update state editMaterial juga
            setEditMaterial(prev => ({...prev, current_stock: newStock}));
          }
        } catch (stockError) {
          console.error('Error updating stock:', stockError);
          toast.error(`Gagal memperbarui stok: ${stockError.message}`);
          
          // Batal edit jika update stok gagal
          return;
        }
      }
      
      // Lanjutkan dengan update data inventori lainnya menggunakan PATCH
      const updateData = {
        name: formData.name,
        category: formData.category,
        unit: formData.unit,
        minimum_stock: parseInt(formData.minimum_stock, 10) || 0,
        price: formData.price.replace(/[^\d]/g, ''),
        location: formData.location,
        notes: formData.notes
      };
      
      console.log("Updating inventory item with data:", updateData);
      
      const response = await axios.patch(
        `https://rumahakrilik.id/api/inventory/${editMaterial.id}/`,
        updateData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      console.log("Update response:", response.data);
      
      toast.success('Data berhasil diperbarui');
      setShowModal(false);
      
      // Pantau apakah refresh data setelah update berjalan dengan baik
      console.log("Refreshing data after update...");
      await fetchMaterials(true);
      
    } catch (error) {
      console.error('Error updating material:', error);
      toast.error(`Gagal memperbarui data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }; 

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus bahan baku ini?')) {
      try {
        const response = await fetchWithAuth(`https://rumahakrilik.id/api/inventory/${id}/`, {
          method: 'DELETE'
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        toast.success('Bahan baku berhasil dihapus');
        fetchMaterials(true);
      } catch (error) {
        console.error('Error deleting material:', error);
        toast.error('Gagal menghapus bahan baku');
      }
    }
  };

  const validateForm = () => {
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Nama bahan baku harus diisi');
      return false;
    }
    
    if (!formData.category) {
      toast.error('Kategori harus dipilih');
      return false;
    }
    
    if (!formData.unit) {
      toast.error('Unit harus dipilih');
      return false;
    }
    
    return true;
  };

  const handleEdit = async (id) => {
    try {
      setLoading(true);
      
      const response = await fetchWithAuth(`https://rumahakrilik.id/api/inventory/${id}/?nocache=${Date.now()}`);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const materialData = await response.json();
      
      setEditMaterial(materialData);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching material details:', error);
      toast.error('Gagal memuat detail bahan baku');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditMaterial(null);
    setFormData({
      name: '',
      category: 'raw_material',
      unit: 'pcs',
      current_stock: '0',
      minimum_stock: '0',
      price: '0',
      location: '',
      notes: ''
    });
  };

  const refreshData = () => {
    fetchMaterials(true);
  };

  const triggerInventorySync = async (token) => {
    try {
      toast.info("Melakukan sinkronisasi inventory...");
      
      // Gunakan satu endpoint yang konsisten di semua kode
      const primaryEndpoint = '/api/inventory-sync/';
      
      try {
        const response = await axios.post(`https://rumahakrilik.id${primaryEndpoint}`, {}, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.status === 200) {
          const data = response.data;
          const updateCount = data.updated_count || 0;
          toast.success(`Sinkronisasi inventory berhasil, ${updateCount} item diperbarui`);
          return true;
        }
      } catch (error) {
        console.error(`Gagal dengan endpoint utama ${primaryEndpoint}:`, error);
        
        // Coba endpoint yang berbeda secara berurutan sebagai fallback
        const fallbackEndpoints = [
          '/api/inventory/sync/',
          '/api/sync-inventory/',
          '/debug-sync/'
        ];
        
        for (const endpoint of fallbackEndpoints) {
          try {
            console.log(`Trying fallback endpoint: ${endpoint}`);
            const response = await axios.post(`https://rumahakrilik.id${endpoint}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.status === 200) {
              toast.success(`Sinkronisasi berhasil dengan endpoint alternatif: ${endpoint}`);
              return true;
            }
          } catch (err) {
            console.log(`Gagal dengan ${endpoint}:`, err);
          }
        }
      }
      
      // Jika semua gagal
      toast.warning("Sinkronisasi inventory gagal, mencoba dengan endpoint emergency...");
      
      try {
        // Terakhir, coba gunakan endpoint debug sebagai fallback terakhir
        const debugResponse = await axios.post(`https://rumahakrilik.id/debug-sync/`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (debugResponse.status === 200) {
          toast.success("Emergency sync berhasil!");
          return true;
        }
      } catch (debugError) {
        console.error("Emergency sync juga gagal:", debugError);
      }
      
      toast.error("Semua upaya sinkronisasi gagal, silakan coba lagi nanti");
      return false;
    } catch (error) {
      console.error("Error triggering inventory sync:", error);
      toast.error(`Error sinkronisasi: ${error.message}`);
      return false;
    }
  };

  const handleSinkronisasi = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      toast.info('Melakukan sinkronisasi inventory...');
      
      // Coba endpoint dengan GET terlebih dahulu (dari konsol terlihat GET berhasil)
      const endpoints = [
        '/debug-sync/',
        '/api/inventory-sync/',
        '/api/inventory/sync/',
        '/api/sync-inventory/'
      ];
      
      let syncSuccess = false;
      
      // Try GET requests first
      for (const endpoint of endpoints) {
        if (syncSuccess) break;
        
        try {
          console.log(`Trying GET ${endpoint}`);
          const response = await axios.get(`https://rumahakrilik.id${endpoint}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          if (response.status === 200) {
            syncSuccess = true;
            const updateCount = response.data.updated_count || 0;
            toast.success(`Sinkronisasi berhasil dengan GET ${endpoint}. ${updateCount} item diperbarui.`);
            setTimeout(() => fetchMaterials(true), 1000);
            break;
          }
        } catch (error) {
          console.error(`Failed with GET ${endpoint}:`, error);
        }
      }
      
      // If all GET requests fail, try POST
      if (!syncSuccess) {
        for (const endpoint of endpoints) {
          try {
            console.log(`Trying POST ${endpoint}`);
            const response = await axios.post(`https://rumahakrilik.id${endpoint}`, {}, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            });
            
            if (response.status === 200) {
              syncSuccess = true;
              const updateCount = response.data.updated_count || 0;
              toast.success(`Sinkronisasi berhasil dengan POST ${endpoint}. ${updateCount} item diperbarui.`);
              setTimeout(() => fetchMaterials(true), 1000);
              break;
            }
          } catch (error) {
            console.error(`Failed with POST ${endpoint}:`, error);
          }
        }
      }
      
      // Fallback if all sync attempts fail
      if (!syncSuccess) {
        toast.warning("Semua metode sinkronisasi gagal, mencoba refresh data langsung");
        fetchMaterials(true);
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(`Gagal sinkronisasi: ${error.message}`);
      fetchMaterials(true); // Refresh data as fallback
    } finally {
      setLoading(false);
    }
  };

  const checkSyncStatus = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      toast.info('Memeriksa status sinkronisasi backend...');
      
      try {
        // Gunakan axios seperti di StockProdukJadi.jsx
        const response = await axios.get('https://rumahakrilik.id/api/inventory/status/', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.status === 200) {
          const data = response.data;
          toast.success(`Status inventory: ${data.inventory_count || 0} item, ${data.transaction_count || 0} transaksi.`);
          
          // Refresh data untuk memastikan UI selalu update
          fetchMaterials(true);
        } else {
          toast.error(`Gagal mendapatkan status inventory: ${response.status}`);
        }
      } catch (error) {
        console.error('Status check failed:', error);
        
        // Coba dengan endpoint alternatif seperti di DaftarAset.jsx
        try {
          const altResponse = await axios.get('https://rumahakrilik.id/api/inventory/', {
            params: { limit: 1 },
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache' 
            }
          });
          
          if (altResponse.status === 200) {
            const data = Array.isArray(altResponse.data) ? altResponse.data : [];
            toast.info(`API tersedia, ${data.length} item ditemukan.`);
            fetchMaterials(true);
          } else {
            toast.error('Gagal memeriksa status dengan metode alternatif');
          }
        } catch (altError) {
          toast.error('Semua metode pemeriksaan gagal');
        }
      }
    } catch (error) {
      console.error('Error checking inventory status:', error);
      toast.error('Gagal memeriksa status inventory');
    } finally {
      setLoading(false);
    }
  };

  const testApi = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      toast.info('Menguji koneksi API, silakan tunggu...');
      
      const endpoints = [
        { url: 'https://rumahakrilik.id/api/inventory/', name: 'Inventory List' },
        { url: 'https://rumahakrilik.id/api/inventory/status/', name: 'Inventory Status' },
        { url: 'https://rumahakrilik.id/health/', name: 'Health Check' }
      ];
      
      const results = [];
      
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint.url, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache'
            },
            cache: 'no-store'
          });
          
          const statusText = response.ok ? 'OK' : 'Error';
          results.push(`${endpoint.name}: ${response.status} ${statusText}`);
        } catch (endpointError) {
          results.push(`${endpoint.name}: Failed - ${endpointError.message}`);
        }
      }
      
      console.log('API Test Results:', results);
      toast.info(`Test API Results:\n${results.join('\n')}`, {
        autoClose: 5000
      });
      
    } catch (error) {
      console.error('API Test failed:', error);
      toast.error(`Test API gagal: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk menguji endpoint sinkronisasi secara spesifik
  const testSyncEndpoints = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      toast.info('Menguji endpoint sinkronisasi, silakan tunggu...');
      
      const endpoints = [
        { method: 'POST', url: 'https://rumahakrilik.id/api/inventory/manual-sync/', name: 'Manual Sync' },
        { method: 'POST', url: 'https://rumahakrilik.id/debug-sync/', name: 'Debug Sync' },
        { method: 'GET', url: 'https://rumahakrilik.id/api/inventory/status/', name: 'Inventory Status' },
        { method: 'GET', url: 'https://rumahakrilik.id/api/inventory/', name: 'Get Inventory List' }
      ];
      
      const results = [];
      
      for (const endpoint of endpoints) {
        try {
          const config = {
            method: endpoint.method,
            url: endpoint.url,
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache'
            }
          };
          
          const response = await axios(config);
          
          results.push(`✅ ${endpoint.name}: ${response.status} OK`);
        } catch (endpointError) {
          const status = endpointError.response ? endpointError.response.status : 'Error';
          results.push(`❌ ${endpoint.name}: ${status} - ${endpointError.message}`);
        }
      }
      
      console.log('Endpoints Test Results:', results);
      toast.info(`Hasil Test Endpoint:\n${results.join('\n')}`, {
        autoClose: 8000
      });
      
    } catch (error) {
      console.error('API Test failed:', error);
      toast.error(`Test API gagal: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Tambahkan fungsi untuk menguji endpoint yang berfungsi
  const findWorkingEndpoint = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      if (!token) {
        toast.error('Sesi login telah berakhir');
        return;
      }
      
      toast.info('Mencari endpoint yang berfungsi...');
      
      const endpoints = [
        { method: 'POST', url: '/api/inventory-sync/' },
        { method: 'POST', url: '/api/inventory/sync/' },
        { method: 'POST', url: '/api/sync-inventory/' },
        { method: 'POST', url: '/debug-sync/' },
        { method: 'GET', url: '/api/inventory/status/' },
        { method: 'GET', url: '/api/inventory/' }
      ];
      
      const results = [];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Testing ${endpoint.method} ${endpoint.url}`);
          
          if (endpoint.method === 'GET') {
            const response = await axios.get(`https://rumahakrilik.id${endpoint.url}`, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache'
              }
            });
            
            results.push(`✅ ${endpoint.method} ${endpoint.url}: ${response.status} OK`);
          } else {
            const response = await axios.post(`https://rumahakrilik.id${endpoint.url}`, {}, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache'
              }
            });
            
            results.push(`✅ ${endpoint.method} ${endpoint.url}: ${response.status} OK`);
            
            // Jika ini adalah endpoint sinkronisasi yang berhasil
            if (endpoint.url.includes('sync')) {
              toast.success(`Endpoint sinkronisasi ditemukan: ${endpoint.url}`);
              localStorage.setItem('workingSyncEndpoint', endpoint.url);
            }
          }
        } catch (error) {
          const statusCode = error.response?.status || 'Unknown';
          results.push(`❌ ${endpoint.method} ${endpoint.url}: ${statusCode} Error`);
        }
      }
      
      console.log('Endpoint test results:', results);
      toast.info(results.join('\n'), { autoClose: 8000 });
      
    } catch (error) {
      console.error('Error testing endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  // Optimalisasi fungsi getFilteredMaterials() untuk performa lebih baik
  const getFilteredMaterials = () => {
    if (!materials || materials.length === 0) return [];
    
    // Filter berdasarkan kata kunci pencarian terlebih dahulu
    let filteredBySearch = materials;
    if (searchTerm) {
      filteredBySearch = materials.filter(material => 
        material.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Kemudian filter berdasarkan tab yang aktif (kategori)
    const categoryFiltered = filteredBySearch.filter(item => 
      item.category === activeTab || 
      (activeTab === 'raw_material' && !item.category)
    );
    
    // Terakhir filter berdasarkan status stok
    let result = categoryFiltered;
    switch(stockFilter) {
      case 'empty':
        result = categoryFiltered.filter(item => item.current_stock === 0);
        break;
      case 'low':
        result = categoryFiltered.filter(item => 
          item.current_stock > 0 && item.current_stock < item.minimum_stock
        );
        break;
      case 'normal':
        result = categoryFiltered.filter(item => 
          item.current_stock >= item.minimum_stock
        );
        break;
    }
    
    return result;
  };

  const materialCategories = [
    { value: 'raw_material', label: 'Bahan Baku' },
    { value: 'support_material', label: 'Material Pendukung' },
    { value: 'packaging', label: 'Packaging' }
  ];

  const unitOptions = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'kg', label: 'Kilogram' },
    { value: 'g', label: 'Gram' },
    { value: 'l', label: 'Liter' },
    { value: 'ml', label: 'Mililiter' },
    { value: 'm', label: 'Meter' },
    { value: 'cm', label: 'Centimeter' },
    { value: 'lembar', label: 'Lembar' }
  ];

  const getStockStatus = (current, minimum) => {
    if (current <= 0) {
      return <Badge bg="danger">Kosong</Badge>;
    } else if (current <= minimum) {
      return <Badge bg="warning">Stok Rendah</Badge>;
    } else {
      return <Badge bg="success">Tersedia</Badge>;
    }
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (editMaterial) {
      handleEditSubmit(e);
    } else {
      handleCreate();
    }
  };

  const getCategoryLabel = (categoryValue) => {
    const category = materialCategories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  const handlePageChange = (pageNumber) => {
    fetchMaterials(false, pageNumber);
  };

  // Tambahkan helper function untuk mendapatkan teks tombol sesuai tab
  const getAddButtonText = (activeTabKey) => {
    switch(activeTabKey) {
      case 'raw_material':
        return 'Tambah Bahan Baku';
      case 'support_material':
        return 'Tambah Material Pendukung';
      case 'packaging':
        return 'Tambah Packaging';
      default:
        return 'Tambah Item';
    }
  };

  // Tambahkan fungsi untuk menampilkan modal histori
  const handleShowHistory = (item) => {
    setHistoryItem({
      id: item.id,
      name: item.name
    });
    setShowHistoryModal(true);
  };

  return (
    <ErrorBoundary>
      <MainLayout>
        <Container fluid className="py-4">
          <h1 className="mb-4">Daftar Bahan Baku</h1>
          
          <div className="d-flex justify-content-end mb-3">
            <InputGroup style={{ maxWidth: '400px' }} className="me-2">
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Cari berdasarkan nama, kategori, lokasi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <Button 
              variant="outline-info" 
              size="sm"
              className="me-2"
              onClick={testApi}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSearch} className="me-1" /> Test API
            </Button>
            <Button 
              variant="warning" 
              onClick={handleSinkronisasi}
              disabled={loading}
              className="me-2"
            >
              {loading ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faSync} />} Sinkronisasi Stok
            </Button>
            <Button 
              variant="info" 
              onClick={checkSyncStatus}
              disabled={loading}
              className="me-2"
            >
              <FontAwesomeIcon icon={faSync} className="me-1" /> Cek Status Sinkronisasi
            </Button>
            <Button 
              variant="outline-secondary" 
              className="me-2" 
              onClick={() => fetchMaterials(true)}
              disabled={loading}
            >
              {loading ? (
                <><Spinner animation="border" size="sm" className="me-1" /> Memuat...</>
              ) : (
                <><FontAwesomeIcon icon={faSync} className="me-1" /> Refresh Data</>
              )}
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setFormData(prev => ({
                  ...prev,
                  category: activeTab
                }));
                setShowModal(true);
              }}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> {getAddButtonText(activeTab)}
            </Button>
          </div>

          <Tabs
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key)}
            className="mb-3"
          >
            <Tab eventKey="raw_material" title="Bahan Baku">
              <Card>
                <Card.Body>
                  {/* Tombol filter diletakkan di atas tabel */}
                  <div className="mb-3">
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setStockFilter('all')}
                    >
                      Semua
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'empty' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setStockFilter('empty')}
                    >
                      Kosong
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'low' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setStockFilter('low')}
                    >
                      Stok Rendah
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'normal' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setStockFilter('normal')}
                    >
                      Stok Normal
                    </button>
                  </div>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3">Memuat data bahan baku...</p>
                    </div>
                  ) : (
                    <>
                      {getFilteredMaterials().length === 0 ? (
                        <EmptyStateMessage 
                          message={`Tidak ada data ${getCategoryLabel(activeTab)} yang tersedia`} 
                          icon={faBoxes}
                          onRefresh={refreshData}
                          actionText={getAddButtonText(activeTab)}
                          onAction={() => {
                            resetForm();
                            setFormData(prev => ({
                              ...prev,
                              category: activeTab
                            }));
                            setShowModal(true);
                          }}
                        />
                      ) : (
                        <Table responsive striped bordered hover>
                          <thead>
                            <tr>
                              <th>Nama Bahan</th>
                              <th>Kategori</th>
                              <th>Stok Saat Ini</th>
                              <th>Stok Minimum</th>
                              <th>Unit</th>
                              <th>Harga per Unit</th>
                              <th>Status</th>
                              <th>Lokasi</th>
                              <th>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredMaterials().map((material) => (
                              <tr key={material.id}>
                                <td>{material.name}</td>
                                <td>
                                  {getCategoryLabel(material.category)}
                                </td>
                                <td>{material.current_stock}</td>
                                <td>{material.minimum_stock}</td>
                                <td>{material.unit}</td>
                                <td>{formatRupiah(material.price)}</td>
                                <td>
                                  {getStockStatus(material.current_stock, material.minimum_stock)}
                                </td>
                                <td>{material.location}</td>
                                <td>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleEdit(material.id)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    className="me-1"
                                    onClick={() => handleDelete(material.id)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                  <Button 
                                    variant="outline-info" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleShowHistory(material)}
                                  >
                                    <FontAwesomeIcon icon={faHistory} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>
            
            <Tab eventKey="support_material" title="Material Pendukung">
              <Card>
                <Card.Body>
                  {/* Tombol filter diletakkan di atas tabel */}
                  <div className="mb-3">
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setStockFilter('all')}
                    >
                      Semua
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'empty' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setStockFilter('empty')}
                    >
                      Kosong
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'low' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setStockFilter('low')}
                    >
                      Stok Rendah
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'normal' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setStockFilter('normal')}
                    >
                      Stok Normal
                    </button>
                  </div>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3">Memuat data material pendukung...</p>
                    </div>
                  ) : (
                    <>
                      {getFilteredMaterials().length === 0 ? (
                        <EmptyStateMessage 
                          message={`Tidak ada data ${getCategoryLabel(activeTab)} yang tersedia`} 
                          icon={faBoxes}
                          onRefresh={refreshData}
                          actionText={getAddButtonText(activeTab)}
                          onAction={() => {
                            resetForm();
                            setFormData(prev => ({
                              ...prev,
                              category: activeTab
                            }));
                            setShowModal(true);
                          }}
                        />
                      ) : (
                        <Table responsive striped bordered hover>
                          <thead>
                            <tr>
                              <th>Nama Bahan</th>
                              <th>Kategori</th>
                              <th>Stok Saat Ini</th>
                              <th>Stok Minimum</th>
                              <th>Unit</th>
                              <th>Harga per Unit</th>
                              <th>Status</th>
                              <th>Lokasi</th>
                              <th>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredMaterials().map((material) => (
                              <tr key={material.id}>
                                <td>{material.name}</td>
                                <td>
                                  {getCategoryLabel(material.category)}
                                </td>
                                <td>{material.current_stock}</td>
                                <td>{material.minimum_stock}</td>
                                <td>{material.unit}</td>
                                <td>{formatRupiah(material.price)}</td>
                                <td>
                                  {getStockStatus(material.current_stock, material.minimum_stock)}
                                </td>
                                <td>{material.location}</td>
                                <td>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleEdit(material.id)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    className="me-1"
                                    onClick={() => handleDelete(material.id)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                  <Button 
                                    variant="outline-info" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleShowHistory(material)}
                                  >
                                    <FontAwesomeIcon icon={faHistory} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>
            
            <Tab eventKey="packaging" title="Packaging">
              <Card>
                <Card.Body>
                  {/* Tombol filter diletakkan di atas tabel */}
                  <div className="mb-3">
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setStockFilter('all')}
                    >
                      Semua
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'empty' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setStockFilter('empty')}
                    >
                      Kosong
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'low' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setStockFilter('low')}
                    >
                      Stok Rendah
                    </button>
                    <button 
                      className={`btn btn-sm mx-1 ${stockFilter === 'normal' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setStockFilter('normal')}
                    >
                      Stok Normal
                    </button>
                  </div>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3">Memuat data packaging...</p>
                    </div>
                  ) : (
                    <>
                      {getFilteredMaterials().length === 0 ? (
                        <EmptyStateMessage 
                          message={`Tidak ada data ${getCategoryLabel(activeTab)} yang tersedia`} 
                          icon={faBoxes}
                          onRefresh={refreshData}
                          actionText={getAddButtonText(activeTab)}
                          onAction={() => {
                            resetForm();
                            setFormData(prev => ({
                              ...prev,
                              category: activeTab
                            }));
                            setShowModal(true);
                          }}
                        />
                      ) : (
                        <Table responsive striped bordered hover>
                          <thead>
                            <tr>
                              <th>Nama Bahan</th>
                              <th>Kategori</th>
                              <th>Stok Saat Ini</th>
                              <th>Stok Minimum</th>
                              <th>Unit</th>
                              <th>Harga per Unit</th>
                              <th>Status</th>
                              <th>Lokasi</th>
                              <th>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredMaterials().map((material) => (
                              <tr key={material.id}>
                                <td>{material.name}</td>
                                <td>
                                  {getCategoryLabel(material.category)}
                                </td>
                                <td>{material.current_stock}</td>
                                <td>{material.minimum_stock}</td>
                                <td>{material.unit}</td>
                                <td>{formatRupiah(material.price)}</td>
                                <td>
                                  {getStockStatus(material.current_stock, material.minimum_stock)}
                                </td>
                                <td>{material.location}</td>
                                <td>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleEdit(material.id)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    className="me-1"
                                    onClick={() => handleDelete(material.id)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                  <Button 
                                    variant="outline-info" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleShowHistory(material)}
                                  >
                                    <FontAwesomeIcon icon={faHistory} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>

          <div className="d-flex justify-content-center mt-4">
            {pagination.totalPages > 1 && (
              <Pagination>
                <Pagination.First 
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev 
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                />
                
                {[...Array(pagination.totalPages)].map((_, idx) => {
                  const pageNumber = idx + 1;
                  if (
                    pageNumber === 1 || 
                    pageNumber === pagination.totalPages ||
                    Math.abs(pageNumber - pagination.currentPage) <= 2
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === pagination.currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  } else if (
                    pageNumber === pagination.currentPage - 3 ||
                    pageNumber === pagination.currentPage + 3
                  ) {
                    return <Pagination.Ellipsis key={`ellipsis-${pageNumber}`} />;
                  }
                  return null;
                })}
                
                <Pagination.Next
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                />
                <Pagination.Last
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.totalPages)}
                />
              </Pagination>
            )}
          </div>

          <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                {editMaterial 
                  ? `Edit ${getCategoryLabel(editMaterial.category)}: ${editMaterial.name}` 
                  : `Tambah ${getCategoryLabel(activeTab)} Baru`
                }
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmitForm}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nama Bahan</Form.Label>
                      <Form.Control 
                        type="text" 
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kategori</Form.Label>
                      <Form.Select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        required
                        disabled={!editMaterial} // Disable jika bukan mode edit
                      >
                        {materialCategories.map((category) => (
                          <option key={category.value} value={category.value}>{category.label}</option>
                        ))}
                      </Form.Select>
                      {!editMaterial && (
                        <Form.Text className="text-muted">
                          Kategori mengikuti tab yang aktif
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit</Form.Label>
                      <Form.Select
                        value={formData.unit}
                        onChange={(e) => setFormData({...formData, unit: e.target.value})}
                        required
                      >
                        {unitOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stok Saat Ini</Form.Label>
                      <Form.Control 
                        type="number"
                        min="0"
                        required
                        value={formData.current_stock}
                        onChange={(e) => setFormData({...formData, current_stock: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stok Minimum</Form.Label>
                      <Form.Control 
                        type="number"
                        min="0"
                        required
                        value={formData.minimum_stock}
                        onChange={(e) => setFormData({...formData, minimum_stock: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Harga per Unit (Rp)</Form.Label>
                      <Form.Control 
                        type="text"
                        required
                        value={formData.price}
                        onChange={(e) => {
                          const value = e.target.value.replace(/^0+|[^\d]/g, '');
                          setFormData({...formData, price: value});
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Lokasi</Form.Label>
                      <Form.Control 
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Catatan</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>
                
                <div className="d-flex justify-content-end">
                  <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                    Batal
                  </Button>
                  <Button variant="primary" type="submit">
                    {editMaterial ? 'Perbarui' : 'Simpan'}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          <HistoryModal 
            show={showHistoryModal} 
            onHide={() => setShowHistoryModal(false)}
            itemId={historyItem.id}
            itemName={historyItem.name}
          />
        </Container>
      </MainLayout>
    </ErrorBoundary>
  );
};

const HistoryModal = ({show, onHide, itemId, itemName}) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  
  useEffect(() => {
    if (show && itemId) {
      fetchItemHistory(itemId);
    }
  }, [show, itemId]);
  
  const fetchItemHistory = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      const response = await axios.get(`https://rumahakrilik.id/api/inventory/${id}/history/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching item history:', error);
      toast.error('Gagal memuat histori item');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Histori {itemName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Memuat histori...</p>
          </div>
        ) : (
          <>
            {history.length === 0 ? (
              <p className="text-center">Tidak ada data histori untuk item ini</p>
            ) : (
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Tipe</th>
                    <th>Jumlah</th>
                    <th>Pemohon</th>
                    <th>Diproses Oleh</th>
                    <th>Referensi</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item, index) => (
                    <tr key={index}>
                      <td>{new Date(item.timestamp).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</td>
                      <td>
                        <Badge bg={item.type === 'in' ? 'success' : 'danger'}>
                          {item.type === 'in' ? 'Masuk' : 'Keluar'}
                        </Badge>
                      </td>
                      <td>{item.quantity}</td>
                      <td>{item.requested_by || '-'}</td>
                      <td>{item.handled_by || '-'}</td>
                      <td>{item.reference || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Tutup</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DaftarStockBahan;